import SectionWrapper from '../SectionWrapper';
import { Link, Flex, Grid, Text } from '@chakra-ui/react';
import ImageUploadField from '../ImageUploadField';
import { useAppSelector, useAppDispatch } from '../../../../store/redux/hooks';
import WatermarkFields from '../WatermarkFields';
import { useEffect, useMemo } from 'react';
import { setWatermarkProp } from '../../../../store/redux/slices/template.slice';
import { ImageField } from '../../../../store/types/template';
import { isImageRequired } from '../utils/imageHelpers';
import { useTemplateFieldValidation } from '../validation/useTemplateFieldValidation';
import { STAMP_BANNER_FIELDS } from '../utils/stampCardConstants';

const BannersSection = ({
  watermarkEnabled,
  onOpenInspirationModal,
}: {
  watermarkEnabled: boolean;
  onOpenInspirationModal?: () => void;
}) => {
  const templateType = useAppSelector((s) => s.template.templateType);
  const platform = 'shared';

  const stampCount = useAppSelector((s) => s.template.stampCount);
  const numericStampCount = Math.min(Math.max(Number(stampCount) || 2, 2), 12);

  const dispatch = useAppDispatch();

  const stampBannerFieldsToShow = useMemo(
    () => STAMP_BANNER_FIELDS.slice(0, numericStampCount + 1),
    [numericStampCount]
  );
  const images = useAppSelector((s) => s.template.images);
  const { validateSection, validateField } =
    useTemplateFieldValidation('images');
  const errors = useAppSelector((s) => s.templateErrors);

  useEffect(() => {
    if (!watermarkEnabled) {
      dispatch(
        setWatermarkProp({
          platform: platform,
          field: 'watermarkColor',
          value: '',
        })
      );
      dispatch(
        setWatermarkProp({
          platform: platform,
          field: 'watermarkPosition',
          value: '',
        })
      );
    } else {
      const currentWatermarkColor = images[platform].watermarkColor;
      const currentWatermarkPosition = images[platform].watermarkPosition;

      if (!currentWatermarkColor) {
        dispatch(
          setWatermarkProp({
            platform: platform,
            field: 'watermarkColor',
            value: 'light',
          })
        );
      }
      if (!currentWatermarkPosition) {
        dispatch(
          setWatermarkProp({
            platform: platform,
            field: 'watermarkPosition',
            value: 'bottomRight',
          })
        );
      }
    }
  }, [watermarkEnabled, platform, images, dispatch]);

  return (
    <SectionWrapper
      id="banners-section"
      title="Banners"
      openOnLoad={true}
      subtitle={
        <Text>
          <Link
            variant="grey"
            textDecoration="underline"
            as="button"
            type="button"
            lineHeight="inherit"
            fontWeight="inherit"
            fontSize="inherit"
            onClick={() => onOpenInspirationModal?.()}
          >
            Need inspiration?
          </Link>{' '}
          See how banners can be used in stamp card designs.
        </Text>
      }
    >
      <Flex direction="column" gap={10}>
        <Flex direction="column" gap={6}>
          <Grid
            templateColumns={{ base: 'repeat(1, 1fr)', xl: 'repeat(2, 1fr)' }}
            columnGap={8}
            rowGap={3}
          >
            {stampBannerFieldsToShow.map(({ field, label }) => (
              <ImageUploadField
                key={field}
                label={label}
                field={field}
                platform={platform}
                isRequired={isImageRequired(
                  templateType,
                  platform,
                  field,
                  images
                )}
                errors={errors}
                validateSection={validateSection}
                validateField={validateField}
              />
            ))}
          </Grid>
          <Text textStyle="bodyFamilyRegular" fontSize="12px">
            Use wide, rectangular images. Recommended file type is png.
            Recommended size: 1125 x 432px
          </Text>
        </Flex>
        {watermarkEnabled && <WatermarkFields platform={platform} />}
      </Flex>
    </SectionWrapper>
  );
};

export default BannersSection;
